import { useI18n } from 'vue-i18n'
import moment from 'moment'

export function useDateFormatter() {
  const { t, locale } = useI18n()

  // Configure moment locale based on current i18n locale
  const configureMomentLocale = () => {
    const localeMap: Record<string, string> = {
      'en': 'en',
      'ro': 'ro',
      'fr': 'fr',
      'it': 'it',
      'de': 'de',
      'es': 'es'
    }

    const momentLocale = localeMap[locale.value] || 'en'
    
    // Set moment locale
    moment.locale(momentLocale)
  }

  // Format date with translated day and month names
  const formatFlightDate = (dateString: string, format: string = 'ddd DD MMMM') => {
    configureMomentLocale()
    
    const date = moment(dateString, 'YYYY-MM-DD')
    
    if (!date.isValid()) {
      return dateString
    }

    // Get the formatted date from moment
    const formatted = date.format(format)
    
    // Replace English day/month names with translated ones
    const dayMap: Record<string, string> = {
      'Sun': t('date.days.sunday').substring(0, 3),
      'Mon': t('date.days.monday').substring(0, 3),
      'Tue': t('date.days.tuesday').substring(0, 3),
      'Wed': t('date.days.wednesday').substring(0, 3),
      'Thu': t('date.days.thursday').substring(0, 3),
      'Fri': t('date.days.friday').substring(0, 3),
      'Sat': t('date.days.saturday').substring(0, 3),
      'Sunday': t('date.days.sunday'),
      'Monday': t('date.days.monday'),
      'Tuesday': t('date.days.tuesday'),
      'Wednesday': t('date.days.wednesday'),
      'Thursday': t('date.days.thursday'),
      'Friday': t('date.days.friday'),
      'Saturday': t('date.days.saturday')
    }

    const monthMap: Record<string, string> = {
      'Jan': t('date.months.january'),
      'Feb': t('date.months.february'),
      'Mar': t('date.months.march'),
      'Apr': t('date.months.april'),
      'May': t('date.months.may'),
      'Jun': t('date.months.june'),
      'Jul': t('date.months.july'),
      'Aug': t('date.months.august'),
      'Sep': t('date.months.september'),
      'Oct': t('date.months.october'),
      'Nov': t('date.months.november'),
      'Dec': t('date.months.december'),
      'January': t('date.monthsFull.january'),
      'February': t('date.monthsFull.february'),
      'March': t('date.monthsFull.march'),
      'April': t('date.monthsFull.april'),
      // 'May' removed to fix build error
      'June': t('date.monthsFull.june'),
      'July': t('date.monthsFull.july'),
      'August': t('date.monthsFull.august'),
      'September': t('date.monthsFull.september'),
      'October': t('date.monthsFull.october'),
      'November': t('date.monthsFull.november'),
      'December': t('date.monthsFull.december')
    }

    // Replace day and month names
    let result = formatted
    
    // First replace abbreviated months
    Object.entries(monthMap).forEach(([en, translated]) => {
      if (en.length <= 3) { // Only abbreviated months (Jan, Feb, Mar, etc.)
        result = result.replace(new RegExp(`\\b${en}\\b`, 'g'), translated)
      }
    })
    
    // Then replace full month names
    Object.entries(monthMap).forEach(([en, translated]) => {
      if (en.length > 3) { // Only full month names (January, February, etc.)
        result = result.replace(new RegExp(`\\b${en}\\b`, 'g'), translated)
      }
    })

    return result
  }

  // Format time with locale-aware formatting
  const formatTime = (timeString: string, format: string = 'HH:mm') => {
    configureMomentLocale()
    
    const time = moment(timeString, 'HH:mm')
    
    if (!time.isValid()) {
      return timeString
    }

    return time.format(format)
  }

  return {
    formatFlightDate,
    formatTime,
    configureMomentLocale
  }
}
